import React from "react"
import {Checkbox} from "antd"
import {v4 as uuid} from "uuid"
import {isBoolean} from "lodash"
import {withTranslation} from "react-i18next"
import {Model} from "Model"
import {BaseInput} from "components"
import {handleError, scrollTableToBottom, toastError, toastWarning} from "helpers"
import {phasesService} from "services"
import {KlassappTableHOC} from "HOC"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTableProps} from "types/common"
import styles from "./Phases.module.css"

type PageProps = {
    t: Function
    model: Model
}

type Props = KlassappTableProps & PageProps

type State = {
    newItemData: any
    oldItemData: any
    isSubmitting: boolean
}

class PhaseTab extends React.Component<Props, State> {
    state = {
        newItemData: null,
        oldItemData: null,
        isSubmitting: false
    }

    componentDidMount() {
        this.props.dispatchFunc([
            {key: "getPageTitle", func: this.getPageTitle},
            {key: "getListData", func: this.getSettings},
            {key: "getFields", func: this.getFields},
            {key: "getColumns", func: this.getColumns},
            {key: "getTableHeaderActions", func: this.getTableHeaderActions},
            {key: "getMenuActions", func: this.getMenuActions},
            {key: "onClickDeleteMulti", func: this.onClickDeleteMulti},
            {key: "onClickEdit", func: this.onClickEdit},
            {key: "onClickDelete", func: this.onClickDelete}
        ])
        this.getSettings()
    }

    getPageTitle = () => {
        return this.props.t("settings.phases")
    }

    getFields = () => {
        const {t} = this.props
        return [t("settings.name"), t("settings.isActive")]
    }

    getTableHeaderActions = (isShowDuplicateBtn = false, checkedData = []) => {
        const hasLockedItem = (checkedData || []).some((item) => item.isLock)
        const {t, onClickShowConfirmModal} = this.props
        const actions = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            })
        }
        return actions
    }

    getMenuActions = () => {
        const {t} = this.props
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: this.onClickEdit,
                canShow: () => {
                    return true
                }
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: this.onClickDelete,
                canShow: () => {
                    return true
                }
            }
        ]
    }

    getSettings = async () => {
        this.props.dispatch({isLoading: true})
        try {
            const {data, total} = await phasesService.getAll()
            const newData = data.map((item) => {
                item.id = item.phaseId
                item.isActiveHtml = this.renderIsActiveHtml(item)
                item.nameHtml = item.name
                return item
            })
            this.props.dispatch({data: newData, total})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    renderIsActiveHtml = (item = null) => {
        if (item) {
            return (
                <div key={`show-${item.phaseId}`} className={styles.visibleCheckboxWrap}>
                    <Checkbox checked={item.isActive} />
                </div>
            )
        }
        const newItemData = this.state.newItemData || {}
        return (
            <div key={`edit-${newItemData.phaseId}`} className={styles.visibleCheckboxWrap}>
                <Checkbox
                    checked={newItemData.isActive}
                    onChange={(event) => this.onChangeNewItemData("isActive", event.target.checked)}
                />
            </div>
        )
    }

    getColumns = () => {
        const {t} = this.props

        return [
            {
                title: t("settings.name"),
                field: "nameHtml"
            },
            {
                title: t("settings.isActive"),
                field: "isActiveHtml"
            }
        ]
    }

    onClickDeleteMulti = async () => {
        const checkedItems = this.props.data.filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.phaseId)
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await phasesService.delete({ids: payload})
            await this.getSettings()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickEdit = (editItem) => {
        const {newItemData} = this.state
        if (newItemData) {
            toastWarning(this.props.t("common:validation.saveBeforeEdit"))
            return
        }
        this.setState(
            {
                newItemData: {...editItem, nameData: editItem.name, isActive: !!editItem.isActive},
                oldItemData: {...editItem}
            },
            () => {
                const newData = this.props.data.map((item) => {
                    if (item.phaseId === editItem.phaseId) {
                        item.isActiveHtml = this.renderIsActiveHtml()
                        item.nameHtml = this.renderNameInput()
                        return item
                    }
                    return item
                })
                this.props.dispatch({data: newData})
            }
        )
    }

    onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            this.setState({newItemData: null, oldItemData: null})
            await this.getSettings()
            return
        }
        try {
            this.props.dispatch({isLoading: true})
            this.setState({newItemData: null, oldItemData: null})
            await phasesService.delete({ids: [deletedItem.phaseId]})
            await this.getSettings()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onChangeNewItemData = (key: string, value: any) => {
        const {newItemData} = this.state
        newItemData[key] = value
        const newData = this.props.data.map((item) => {
            if (item.id === newItemData.id) {
                item.nameHtml = this.renderNameInput()
                item.isActiveHtml = this.renderIsActiveHtml()
                return item
            }
            return item
        })
        this.setState({newItemData})
        this.props.dispatch({data: newData})
    }

    renderNameInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.nameData}
                placeholder="Name"
                onChange={(value) => this.onChangeNewItemData("nameData", value)}
            />
        )
    }

    onClickAddItem = () => {
        const newItem = {
            id: uuid(),
            nameHtml: this.renderNameInput(),
            isActive: false,
            isActiveHtml: this.renderIsActiveHtml(),
            isForm: true
        }
        const {data} = this.props
        data.push(newItem)
        this.setState({newItemData: newItem, oldItemData: null})
        this.props.dispatch({data})
        scrollTableToBottom(this.props.tableRef)
    }

    validateDataBeforeSubmit = () => {
        const {isActive, nameData} = this.state.newItemData
        const {t} = this.props
        if (!nameData) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.name")
                })
            )
            return false
        }
        if (!isBoolean(isActive)) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.isActive")
                })
            )
            return false
        }
        return true
    }

    onClickSave = async () => {
        if (!this.validateDataBeforeSubmit()) {
            return
        }
        const {nameData, isActive, isForm, id} = this.state.newItemData
        if (isForm) {
            const submitData = {
                name: nameData,
                isActive
            }
            await this.createSetting(submitData)
        } else {
            const submitData = {name: nameData, isActive}
            await this.updateSetting(id, submitData)
        }
        this.setState({newItemData: null, oldItemData: null}, () => {
            this.getSettings()
        })
    }

    createSetting = async (data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await phasesService.create(data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    updateSetting = async (phaseId, data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await phasesService.update({...data, phaseId})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    onClickCancel = () => {
        const {oldItemData} = this.state
        const newData = this.props.data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = oldItemData.name
                    item.isActive = oldItemData.isActive
                    item.isActiveHtml = this.renderIsActiveHtml(item)
                    return item
                }
                return item
            })
        this.setState({newItemData: null, oldItemData: null})
        this.props.dispatch({data: newData})
    }

    render() {
        const {newItemData, isSubmitting} = this.state
        const {
            t,
            page,
            total,
            pageSize,
            columns,
            data,
            menuActions,
            allFields,
            fields,
            tableHeaderActions,
            isLoading,
            isHideMenuActions,
            isShowTableHeaderAction
        } = this.props

        return (
            <div className={styles.wrapper}>
                <KlassappTableHeader
                    isShowAction={isShowTableHeaderAction}
                    actions={tableHeaderActions}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={this.props.onChangePage}
                    onChangeRowPerPage={this.props.onChangeRowPerPage}
                    fields={fields}
                    allFields={allFields}
                    onChangeFields={this.props.onChangeFields}
                    onChangeAllFields={this.props.onChangeAllFields}
                    onDraggableColumn={this.props.onDraggableColumn}
                />
                <KlassappTable
                    columns={columns}
                    data={data}
                    menuActions={isHideMenuActions ? [] : menuActions}
                    isLoading={isLoading}
                    fields={fields}
                    allFields={allFields}
                    isShowCheckedColumn
                    onClickRowItem={() => {}}
                    tableRef={this.props.tableRef}
                    onChangeFields={this.props.onChangeFields}
                    onUpdateRowData={this.props.onUpdateRowData}
                    onUpdateTableData={this.props.onUpdateTableData}
                    onClickSortColumn={this.props.onClickSortColumn}
                    onDraggableColumn={this.props.onDraggableColumn}
                    onChangeAllFields={this.props.onChangeAllFields}
                />
                {!newItemData ? (
                    !isLoading && <AddItemCircleButton onClick={this.onClickAddItem} />
                ) : (
                    <div className={styles.buttonWrap}>
                        <SecondaryButton
                            title={t("common:action.cancel")}
                            onClick={this.onClickCancel}
                            className={styles.cancelBtn}
                        />
                        <BaseButton
                            title={t("common:action.save").toUpperCase()}
                            onClick={this.onClickSave}
                            loading={isSubmitting}
                        />
                    </div>
                )}
            </div>
        )
    }
}

export const Phase = KlassappTableHOC(withTranslation(["academics", "common"])(PhaseTab))
